// src/pages/MotorcycleForm/adapters.js

import { uid } from "./utils/id";
import { normalizeRangesEdit } from "./utils/tiers";

const numOrNull = (value) => {
    if (
        value === "" ||
        value === null ||
        value === undefined
    ) {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
};

const numOrZero = (value) => {
    if (
        value === "" ||
        value === null ||
        value === undefined
    ) {
        return 0;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
        ? parsed
        : 0;
};

/**
 * Backend price starts -> UI ranges.
 *
 * Backend:
 * [
 *   {
 *     id?,
 *     rentalDays,
 *     lowSeasonPrice,
 *     mediumSeasonPrice,
 *     highSeasonPrice
 *   }
 * ]
 *
 * UI:
 * [
 *   {
 *     _id,
 *     start,
 *     end,
 *     low,
 *     medium,
 *     high
 *   }
 * ]
 */
export function pricesStartsToRanges(
    starts = [],
    seasonal = false
) {
    const sorted = [
        ...(Array.isArray(starts)
            ? starts
            : []),
    ].sort(
        (left, right) =>
            Number(left?.rentalDays || 0) -
            Number(right?.rentalDays || 0)
    );

    const ranges = [];

    for (
        let index = 0;
        index < sorted.length;
        index += 1
    ) {
        const current = sorted[index];
        const next = sorted[index + 1];

        const low = Number(
            current?.lowSeasonPrice ?? 0
        );

        const medium = seasonal
            ? Number(
                current?.mediumSeasonPrice ??
                low
            )
            : 0;

        const high = seasonal
            ? Number(
                current?.highSeasonPrice ??
                low
            )
            : 0;

        ranges.push({
            _id:
                current?.id ??
                uid(),

            start: Number(
                current?.rentalDays || 1
            ),

            end: next
                ? Number(next.rentalDays) - 1
                : null,

            low,
            medium,
            high,
        });
    }

    const fallback = [
        {
            _id: uid(),
            start: 1,
            end: null,
            low: 0,
            medium: 0,
            high: 0,
        },
    ];

    return normalizeRangesEdit(
        ranges.length
            ? ranges
            : fallback,
        seasonal
    );
}

/**
 * UI ranges -> Backend price starts.
 */
export function rangesToStarts(
    ranges = [],
    seasonal = false
) {
    const sorted = [
        ...(Array.isArray(ranges)
            ? ranges
            : []),
    ].sort(
        (left, right) =>
            Number(left?.start || 0) -
            Number(right?.start || 0)
    );

    return sorted.map((range) => {
        const start = Math.max(
            1,
            parseInt(
                String(
                    range?.start || 1
                ),
                10
            )
        );

        const low = Number(
            range?.low ?? 0
        );

        const medium = seasonal
            ? Number(
                range?.medium ?? low
            )
            : 0;

        const high = seasonal
            ? Number(
                range?.high ?? low
            )
            : 0;

        return {
            rentalDays: start,
            lowSeasonPrice: low,
            mediumSeasonPrice: medium,
            highSeasonPrice: high,
        };
    });
}

/**
 * Backend extras -> Formik extras.
 *
 * shopExtraId 可以保留在表单状态中，用于显示和识别原始记录。
 * 但是 buildRegularExtrasPayload() 不会把它提交给后端。
 */
export function normalizeExtras(extras) {
    if (!Array.isArray(extras)) {
        return [];
    }

    return extras.map((extra) => {
        const shopExtraId =
            extra?.ShopExtra?.id ??
            extra?.shopExtraId ??
            null;

        return {
            ...extra,

            _id:
                extra?._id ??
                uid(),

            shopExtraId,
        };
    });
}

/**
 * Backend availability -> Formik availability.
 */
export function normalizeAvailability(list) {
    if (!Array.isArray(list)) {
        return [];
    }

    return list.map((availability) => ({
        ...availability,

        _id:
            availability?._id ??
            uid(),
    }));
}

/**
 * 判断数据是否实际存在季节价格差异。
 */
export function detectSeasonalPricing(
    starts = []
) {
    return (
        Array.isArray(starts)
            ? starts
            : []
    ).some((price) => {
        const low = Number(
            price?.lowSeasonPrice ?? 0
        );

        const medium = Number(
            price?.mediumSeasonPrice ?? 0
        );

        const high = Number(
            price?.highSeasonPrice ?? 0
        );

        return (
            low !== medium ||
            low !== high
        );
    });
}

const MANAGED_EXTRA_KEYS = new Set([
    "cdw",
    "cdw2",
]);

const normalizeExtraKey = (extra) =>
    String(
        extra?.key ??
        extra?.Extra?.key ??
        extra?.ShopExtra?.Extra?.key ??
        ""
    )
        .trim()
        .toLowerCase();

const getGlobalExtraId = (extra) =>
    extra?.extraId ??
    extra?.Extra?.id ??
    extra?.ShopExtra?.extraId ??
    extra?.ShopExtra?.Extra?.id ??
    null;

const nullableInteger = (value) => {
    if (
        value === "" ||
        value === null ||
        value === undefined
    ) {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
        ? Math.trunc(parsed)
        : null;
};

/**
 * Formik regular extras -> Backend ExtraDto[].
 *
 * 这里只提交 DTO 白名单字段。
 *
 * 明确不提交：
 * - ShopMotorcycleExtra.id
 * - _id
 * - shopExtraId
 * - ShopExtra
 * - Extra
 *
 * 原因：
 * shopExtraId 只属于某一个具体 Shop。
 * 当前页面会对同一 MotorcycleModel 下的多个 Shop 保存同一份配置，
 * 因此不能把 Shop A 的 shopExtraId 提交给 Shop B。
 *
 * 后端应该根据当前 shopId + extraId/key 找到或创建正确的 ShopExtra。
 */
export const buildRegularExtrasPayload = (
    extras = []
) => {
    if (!Array.isArray(extras)) {
        return [];
    }

    return extras
        .filter((extra) =>
            MANAGED_EXTRA_KEYS.has(
                normalizeExtraKey(extra)
            )
        )
        .map((extra) => {
            const key =
                normalizeExtraKey(extra);

            const extraId =
                getGlobalExtraId(extra);

            return {
                ...(extraId
                    ? {
                        extraId,
                    }
                    : {}),

                key,

                isRequired: Boolean(
                    extra?.isRequired
                ),

                description:
                    typeof extra?.description ===
                        "string"
                        ? extra.description.trim() ||
                        null
                        : null,

                pricePerDay:
                    nullableInteger(
                        extra?.pricePerDay
                    ),

                maxPrice:
                    nullableInteger(
                        extra?.maxPrice
                    ),

                priceType:
                    extra?.priceType ||
                    "PER_DAY",
            };
        });
};

export {
    numOrNull,
    numOrZero,
};